import { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";

//CONTEXTS
import { DarkModeProvider } from "./Context/DarkModeContext";
import { AuthProvider } from "./Context/AuthContext.js";
import { GeoProvider } from "./Context/GeoContext";
import { fetchUserGeo } from "./utils/geo.js";
// import { clearCachedGeo } from "./utils/geo.js";
// import DeveloperRegionOverride from "./Components/DeveloperRegionOverride";

//LAYOUTS
import Layout from "./Components/Layout.jsx";
import GuestOnlyRoute from "./Components/GuestOnlyRoute.jsx";
import LayoutAdmin from "./Components/Layout_Admin.jsx";

//UNPROTECTED Routes
import NoPage from "./pages/NoPage.jsx";
import LandingPage from "./pages/Auth/LandingPage.jsx";
import SignUpPage from "./pages/Auth/SignUpPage.jsx";
import LoginPage from "./pages/Auth/LoginPage.jsx";

//STUDENTS SECTION
import HomePage from "./pages/Student/HomePage.jsx";
import FoldersPage from "./pages/Student/FoldersPage.jsx";
import SubfoldersPage from "./pages/Student/SubfoldersPage.jsx";
import MyCoursesPage from "./pages/Student/MyCoursesPage.jsx";
import CourseContentPage from "./pages/Student/Content/CourseContentPage.jsx";
import Catalogue from "./pages/Student/CataloguePage.jsx";
import StudentVideos from "./pages/Student/Content/videos/StudentVideos.jsx";
import StudentMaterials from "./pages/Student/Content/materials/StudentMaterials.jsx";
import LiveRequestsPage from "./pages/Student/LiveRequestsPage.jsx";
import EditProfilePage from "./pages/Student/EditProfilePage.jsx";
import EditBasicInfoPage from "./pages/Student/EditProfilePage/EditBasicInfoPage.jsx";
import EditPasswordPage from "./pages/Student/EditProfilePage/EditPasswordPage.jsx";
import WalletPage from "./pages/Student/WalletPage.jsx";

//////////////////ADMIN SECTION
import AdminStudents from "./pages/Admin/AdminStudents.jsx";
import UserInfoPage from "./pages/Admin/UserInfoPage.jsx";
import AdminCourses from "./pages/Admin/AdminCourses.jsx";
import AdminCodes from "./pages/Admin/AdminCodes.jsx";
import AdminFolders from "./pages/Admin/AdminFolders.jsx";
import AdminAcademicLevels from "./pages/Admin/AdminAcademicLevels.jsx";
import AdminStores from "./pages/Admin/AdminStores.jsx";

//ADMIN CATALOGUE
import AdminCatalogue from "./pages/Admin/Catalogue/AdminCatalogue.jsx";
import AdminCatalogueVideos from "./pages/Admin/Catalogue/videos/AdminCatalogueVideos.jsx";
import AdminCatalogueMaterials from "./pages/Admin/Catalogue/materials/AdminCatalogueMaterials.jsx";

//ADMIN COURSE CONTENT
import AdminCourseContentPage from "./pages/Admin/Content/AdminCourseContentPage.jsx";
import AdminAnnouncements from "./pages/Admin/Announcements/AdminAnnouncements.jsx";
import AdminVideos from "./pages/Admin/Content/videos/AdminVideos.jsx";
import AdminMaterials from "./pages/Admin/Content/materials/AdminMaterials.jsx";

//ADMIN LIVE REQUESTS
import AdminLiveRequest from "./pages/Admin/AdminLiveRequest.jsx";
import Sessions from "./pages/Admin/AdminLiveRequest/Sessions.jsx";
import SessionRequests from "./pages/Admin/AdminLiveRequest/SessionRequests.jsx";
import InsideSession from "./pages/Admin/AdminLiveRequest/InsideSession.jsx";

// ============================
// DEV ONLY: clear cached geo so the override applies
// ============================
// if (process.env.NODE_ENV === "development") {
//   clearCachedGeo(); // remove previous cached geo
// }

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(null);

  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem("accessToken");
      const refreshToken = localStorage.getItem("refreshToken");
      const role = localStorage.getItem("role");

      if (!token || !refreshToken) {
        localStorage.clear();
        setIsAuthenticated(false);
      } else {
        setIsAuthenticated(role);
      }
    };

    checkAuth();

    // Add a listener to update authentication status when localStorage changes
    window.addEventListener("storage", checkAuth);

    // Cleanup listener on component unmount
    return () => {
      window.removeEventListener("storage", checkAuth);
    };
  }, []);

  useEffect(() => {
    const checkStoreConsistency = async () => {
      const role = localStorage.getItem("role");
      const token = localStorage.getItem("accessToken");
      if (!token || !role) return;

      const fresh = await fetchUserGeo(); // updates cache if expired

      const lastLoginStoreId = localStorage.getItem("lastLogin");

      if (!lastLoginStoreId) {
        localStorage.setItem("lastLogin", fresh.storeId);
        return;
      }

      if (String(lastLoginStoreId) !== String(fresh.storeId)) {
        toast.info(
          "Your detected store changed — please logout and log back in to apply changes.",
        );
      }
    };

    checkStoreConsistency();
  }, []); // run once on app boot

  if (isAuthenticated === null) return null;

  return (
    <Router>
      <GeoProvider>
        {/* <DeveloperRegionOverride /> */}
        <DarkModeProvider>
          <AuthProvider>
            <Routes>
              {/* Signed-in users skip authentication pages, including browser history entries. */}
              <Route element={<GuestOnlyRoute role={isAuthenticated} />}>
                <Route path="/landing" element={<LandingPage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/signup" element={<SignUpPage />} />
              </Route>

              {/* Conditional route handling */}
              <Route
                path="/"
                element={
                  !isAuthenticated ? (
                    <Navigate to="/landing" replace />
                  ) : isAuthenticated === "User" ? (
                    <Navigate to="/home" replace />
                  ) : isAuthenticated === "Admin" ? (
                    <Navigate to="/students" replace />
                  ) : null
                }
              />

              {/* Protected routes with Layout for Users */}
              {isAuthenticated === "User" && (
                <Route path="/" element={<Layout />}>
                  <Route path="/home" element={<HomePage />} />
                  {/* Folder browsing */}
                  <Route path="/student-folders" element={<FoldersPage />} />
                  <Route
                    path="/student-folders/*"
                    element={<SubfoldersPage />}
                  />

                  {/* <Route
                    path="/student-folders/:folderName/subfolders"
                    element={<SubfoldersPage />}
                  /> */}

                  {/* Nested: courses inside folders */}
                  <Route path="/my-courses" element={<MyCoursesPage />} />
                  <Route
                    path="/my-courses/:courseName"
                    element={<CourseContentPage />}
                  />
                  <Route
                    path="/my-courses/:courseId/videos"
                    element={<StudentVideos />}
                  />
                  <Route
                    path="/my-courses/:courseId/materials"
                    element={<StudentMaterials />}
                  />

                  {/* Routes from the folders tab */}
                  <Route
                    path="/student-folders/:folderId/courses/:courseId"
                    element={<CourseContentPage />}
                  />
                  <Route
                    path="/student-folders/:folderId/courses/:courseId/videos"
                    element={<StudentVideos />}
                  />
                  <Route
                    path="/student-folders/:folderId/courses/:courseId/materials"
                    element={<StudentMaterials />}
                  />

                  {/* Keep the rest */}
                  <Route path="live-sessions" element={<LiveRequestsPage />} />
                  <Route path="edit-profile" element={<EditProfilePage />} />
                  <Route
                    path="edit-profile/basic-info"
                    element={<EditBasicInfoPage />}
                  />
                  <Route
                    path="edit-profile/password"
                    element={<EditPasswordPage />}
                  />
                  <Route path="wallet" element={<WalletPage />} />
                  <Route path="catalogue" element={<Catalogue />} />
                </Route>
              )}

              {/* Protected routes with LayoutAdmin for Admins */}
              {isAuthenticated === "Admin" && (
                <Route path="/" element={<LayoutAdmin />}>
                  <Route
                    path="/announcements"
                    element={<AdminAnnouncements />}
                  />

                  <Route path="/students" element={<AdminStudents />} />
                  <Route
                    path="/students/user/:userId"
                    element={<UserInfoPage />}
                  />
                  <Route path="/courses" element={<AdminCourses />} />
                  <Route
                    path="courses/:courseId"
                    element={<AdminCourseContentPage />}
                  />
                  <Route
                    path="courses/:courseId/videos"
                    element={<AdminVideos />}
                  />
                  <Route
                    path="courses/:courseId/materials"
                    element={<AdminMaterials />}
                  />

                  <Route path="/catalogue" element={<AdminCatalogue />} />
                  <Route
                    path="catalogue/videos"
                    element={<AdminCatalogueVideos />}
                  />
                  <Route
                    path="catalogue/materials"
                    element={<AdminCatalogueMaterials />}
                  />

                  {/* Updated live requests routes */}
                  <Route path="live-sessions" element={<AdminLiveRequest />} />
                  <Route path="live-sessions/sessions" element={<Sessions />} />
                  <Route
                    path="sessions/:sessionId"
                    element={<InsideSession />}
                  />
                  <Route
                    path="live-sessions/sessions-requests"
                    element={<SessionRequests />}
                  />

                  <Route path="folders" element={<AdminFolders />} />
                  <Route path="stores" element={<AdminStores />} />
                  <Route
                    path="academic-levels"
                    element={<AdminAcademicLevels />}
                  />
                  <Route path="codes" element={<AdminCodes />} />
                </Route>
              )}

              {/* Catch-all route */}
              <Route path="*" element={<NoPage />} />
            </Routes>
            <ToastContainer
              position="top-right"
              autoClose={2000}
              newestOnTop
              closeOnClick
              // pauseOnHover
              hideProgressBar
              draggable
              style={{ zIndex: 2147483647 }}
            />
            {/* <ToastContainer
              position="top-right"
              autoClose={2500}
              newestOnTop
              closeOnClick
              pauseOnHover
              draggable
              hideProgressBar
              theme="auto"
              toastClassName={() =>
                "!rounded-2xl !border !border-slate-200 dark:!border-white/10 !bg-white/95 dark:!bg-[#1e1e1e]/95 !text-slate-800 dark:!text-white !p-4 !shadow-xl !backdrop-blur-xl"
              }
              bodyClassName="!m-0 !p-0 !text-sm !font-medium"
              style={{
                zIndex: 2147483647,
                width: "min(380px, calc(100vw - 32px))",
              }}
            /> */}
          </AuthProvider>
        </DarkModeProvider>
      </GeoProvider>
    </Router>
  );
}

export default App;
